import {
  canvasHeight,
  canvasWidth,
  DefaultBloomColor,
  DefaultEntityColor,
  initialZoom,
} from "./data/constants";
import { Camera } from "./camera";
import { StageDef } from "./data/maps";
import { Marble } from "./marble";
import { ParticleManager } from "./particleManager";
import { GameObject } from "./gameObject";
import { UIObject } from "./UIObject";
import { VectorLike } from "./types/VectorLike";
import { MapEntityState } from "./types/MapEntity.type";

export type RenderParameters = {
  camera: Camera;
  stage: StageDef;
  entities: MapEntityState[];
  marbles: Marble[];
  winners: Marble[];
  particleManager: ParticleManager;
  effects: GameObject[];
  winnerRank: number;
  winner: Marble | null;
  size: VectorLike;
};

export class RouletteRenderer {
  private _canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  public sizeFactor = 1;

  private _images: { [key: string]: HTMLImageElement } = {};

  constructor() {}

  get width() {
    return this._canvas.width;
  }

  get height() {
    return this._canvas.height;
  }

  get canvas() {
    return this._canvas;
  }

  async init(container?: HTMLElement) {
    await this._load();

    this._canvas = document.createElement("canvas");
    this._canvas.style.width = "100%";
    this._canvas.style.height = "100%";
    this._canvas.style.display = "block";
    this._canvas.width = canvasWidth;
    this._canvas.height = canvasHeight;
    this.ctx = this._canvas.getContext("2d", {
      alpha: false,
    }) as CanvasRenderingContext2D;

    (container || document.body).appendChild(this._canvas);

    const targetEl = (container || this._canvas) as HTMLElement;

    const resizing = (entries?: ResizeObserverEntry[]) => {
      const realSize = entries
        ? entries[0].contentRect
        : targetEl.getBoundingClientRect();
      const width = realSize.width;
      const height = realSize.height;

      const nextW = Math.floor(width);
      const nextH = Math.floor(height);
      if (this._canvas.width !== nextW || this._canvas.height !== nextH) {
        this._canvas.width = nextW;
        this._canvas.height = nextH;
        this.sizeFactor = width / realSize.width;
      }
    };

    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => resizing(entries));
    });

    resizeObserver.observe(targetEl);
    resizing();
  }

  private async _loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((rs) => {
      const img = new Image();
      img.addEventListener("load", () => {
        rs(img);
      });
      img.src = url;
    });
  }

  private async _load(): Promise<void> {
    const assetBase = "/roulette-assets/images/";
    const loadPromises = [
      { name: "챔루", url: assetBase + "chamru.png" },
      { name: "쿠빈", url: assetBase + "kubin.png" },
    ].map(({ name, url }) => {
      return (async () => {
        this._images[name] = await this._loadImage(url);
      })();
    });

    await Promise.all(loadPromises);
  }

  render(renderParameters: RenderParameters, uiObjects: UIObject[]) {
    this.ctx.fillStyle = "black";
    this.ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);

    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "top";
    this.ctx.font = "0.4pt sans-serif";
    this.ctx.lineWidth = 3 / (renderParameters.camera.zoom + initialZoom);
    renderParameters.camera.renderScene(this.ctx, () => {
      this.renderEntities(renderParameters.entities);
      this.renderEffects(renderParameters);
      this.renderMarbles(renderParameters);
    });
    this.ctx.restore();

    uiObjects.forEach((obj) =>
      obj.render(
        this.ctx,
        renderParameters,
        this._canvas.width,
        this._canvas.height
      )
    );
    renderParameters.particleManager.render(this.ctx);
    this.renderWinner(renderParameters);
  }

  private renderEntities(entities: MapEntityState[]) {
    this.ctx.save();
    entities.forEach((entity) => {
      this.ctx.save();
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      this.ctx.fillStyle =
        entity.shape.color ?? DefaultEntityColor[entity.shape.type];
      this.ctx.strokeStyle =
        entity.shape.color ?? DefaultEntityColor[entity.shape.type];
      this.ctx.shadowBlur = 15;
      this.ctx.shadowColor =
        entity.shape.bloomColor ??
        entity.shape.color ??
        DefaultBloomColor[entity.shape.type];
      const shape = entity.shape;
      switch (shape.type) {
        case "polyline":
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case "box":
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        case "circle":
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }

      this.ctx.restore();
    });
    this.ctx.restore();
  }

  private renderEffects({ effects, camera }: RenderParameters) {
    effects.forEach((effect) =>
      effect.render(this.ctx, camera.zoom * initialZoom)
    );
  }

  private renderMarbles({
    marbles,
    camera,
    winnerRank,
    winners,
  }: RenderParameters) {
    const winnerIndex = winnerRank - winners.length;

    marbles.forEach((marble, i) => {
      marble.render(
        this.ctx,
        camera.zoom * initialZoom,
        i === winnerIndex,
        false,
        this._images[marble.name] || undefined
      );
    });
  }

  private renderWinner({ winner }: RenderParameters) {
    if (!winner) return;
    this.ctx.save();
    this.ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    this.ctx.fillRect(
      this._canvas.width / 2,
      this._canvas.height - 168,
      this._canvas.width / 2,
      168
    );
    this.ctx.fillStyle = "white";
    this.ctx.font = "bold 48px sans-serif";
    this.ctx.textAlign = "right";
    this.ctx.fillText(
      "Winner",
      this._canvas.width - 10,
      this._canvas.height - 120
    );
    this.ctx.font = "bold 72px sans-serif";
    this.ctx.fillStyle = winner.color;
    this.ctx.fillText(
      winner.name,
      this._canvas.width - 10,
      this._canvas.height - 55
    );
    this.ctx.restore();
  }
}
